import React, { useContext, useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { Link, useHistory, useLocation } from "react-router-dom"
import { Button } from "../../components/common/button/Button"
import { CountryFlag } from "../../components/common/countryFlag/countryFlag"
import { Input } from "../../components/common/input/Input"
import { Seperator } from "../../components/common/seperator/seperator"
import { TextLink } from "../../components/common/TextLink/TextLink"
import VehicleInfoContainer from "../../components/common/VehicleInfoContainer/VehicleInfoContainer"
import TermsAndConditions from "../../components/TermsAndConditions/TermsAndConditions"
import { AppContext, AppContextInterface } from "../../state/ContextProvider"
import { GeoLocation } from "../../utils/geolocation"
import { GroupIds } from "../../utils/group-utils"
import { ContactNumber } from "../../components/common/contactNumber/contactNumber"
import { checkPath } from "../../utils/validation-utils"
import GAHiddenInputs from "../../components/common/gaHiddenInputs/gaHiddenInputs"

type FormData = {
  firstName: string
  lastName: string
  email: string
  contactNumber: string
  countryCode: string
  termsConditions: boolean
}

const YourDetails: React.FC = () => {
  const history = useHistory()
  const { search } = useLocation()
  const {
    actions: {
      addContactDetails,
      addTermsConditions,
      addAppointmentDetails,
      addClosestDealerships,
    },
    state: {
      contactDetails,
      contactDetails: {
        countryCode,
        firstName,
        lastName,
        email,
        contactNumber,
        postcode,
      },
      termsConditions,
      appointmentDetails,
      closestDealerships,
      appointmentDetails: { postCode },
      groupId,
    },
    api: { getValuationLimit },
  } = useContext(AppContext) as AppContextInterface

  const [postCodeError, setPostCodeError] = useState(false)
  const [dealershipCtaDisabled, setDealershipCtaDisabled] = useState(true)
  const [validPostcode, setValidPostcode] = useState(false)
  const [validPostCodeError, setValidPostCodeError] = useState(false)

  const { handleSubmit, register, errors, control, setValue, watch } =
    useForm<FormData>({
      mode: "all",
      defaultValues: {
        firstName: contactDetails?.firstName,
        lastName: contactDetails?.lastName,
        email: contactDetails?.email,
        contactNumber: contactDetails?.contactNumber,
        countryCode: contactDetails?.countryCode,
        termsConditions: termsConditions,
      },
    })

  const errorMessage = {
    firstName: "Please enter a valid first name",
    lastName: "Please enter a valid last name",
    contactNumber: "Please enter a valid contact number",
    email: "Please enter a valid email",
    dealership:
      "Ooops something has gone wrong, please try again or select another dealership",
    postcode: "Please enter a valid postcode",
  }

  const onSubmit = (data: any) => {
    addContactDetails({
      ...contactDetails,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      contactNumber: data.contactNumber,
      countryCode: data.countryCode,
      postCode: data.postCode,
      termsConditions: data.termsConditions,
    })
  }

  const mobileOrLandlineNumber = watch("contactNumber")

  useEffect(() => {
    return mobileOrLandlineNumber
      ? addContactDetails({
          ...contactDetails,
          contactNumber: mobileOrLandlineNumber,
        })
      : null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileOrLandlineNumber])

  useEffect(() => {
    if (postCodeError && validPostcode) {
      setValidPostCodeError(true)
    }
  }, [postCodeError, validPostcode])

  const checkForNoErrors = Object.keys(errors).length === 0

  const checkForEmptyFields =
    firstName &&
    lastName &&
    email &&
    contactNumber &&
    postcode &&
    termsConditions

  return (
    <div className="your-details-container">
      <VehicleInfoContainer />
      <div className="inner-container">
        <h2 className="contact-details-heading">Contact details</h2>
        <p className="contact-details-paragraph">
          Enter your contact details to get your instant valuation.
        </p>
        <Seperator />
        <div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <GAHiddenInputs />
            <div className="full-name">
              <p className="input-text">First Name *</p>
              <Input
                type="text"
                name="firstName"
                className="contact-input"
                register={register}
                placeholder="Enter your first name"
                rules={{
                  required: errorMessage.firstName,
                  minLength: {
                    value: 2,
                    message: errorMessage.firstName,
                  },
                  maxLength: {
                    value: 50,
                    message: errorMessage.firstName,
                  },
                }}
                onChange={(e) =>
                  addContactDetails({
                    ...contactDetails,
                    firstName: e.target.value,
                  })
                }
                errors={errors}
              />
            </div>
            <div className="full-name">
              <p className="input-text">Last Name *</p>
              <Input
                type="text"
                name="lastName"
                className="contact-input"
                register={register}
                placeholder="Enter your last name"
                rules={{
                  required: errorMessage.lastName,
                  minLength: {
                    value: 2,
                    message: errorMessage.lastName,
                  },
                  maxLength: {
                    value: 50,
                    message: errorMessage.lastName,
                  },
                }}
                onChange={(e) =>
                  addContactDetails({
                    ...contactDetails,
                    lastName: e.target.value,
                  })
                }
                errors={errors}
              />
            </div>
            <div className="email">
              <p className="input-text">Email *</p>
              <Input
                type="text"
                className="contact-input"
                name="email"
                register={register}
                rules={{
                  required: errorMessage.email,
                  pattern: {
                    value:
                      // eslint-disable-next-line no-useless-escape
                      /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    message: errorMessage.email,
                  },
                }}
                onChange={(e) =>
                  addContactDetails({
                    ...contactDetails,
                    email: e.target.value,
                  })
                }
                errors={errors}
                placeholder="Enter your email"
              />
            </div>
            <div className="mobile-number">
              <p className="input-text">Contact number *</p>
              <CountryFlag
                name="countryCode"
                countryCode={countryCode}
                register={register}
                onChange={(e) =>
                  addContactDetails({
                    ...contactDetails,
                    countryCode: e.target.value,
                  })
                }
              />
              <ContactNumber
                name="contactNumber"
                countryCode={countryCode}
                control={control}
                errors={errors}
                errorMessage={errorMessage.contactNumber}
              />
            </div>
            <p className="input-text">Postcode *</p>
            <Input
              className="appointment-input"
              type="text"
              defaultValue={postCode}
              name="postCode"
              register={register}
              placeholder="Enter your postcode"
              rules={{
                required: "Please enter your postcode",
                pattern: {
                  value:
                    // eslint-disable-next-line no-useless-escape
                    /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([AZa-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z]))))[\s]*[0-9][A-Za-z]{2})$/,
                  message: errorMessage.postcode,
                },
              }}
              onChange={(e) => {
                addAppointmentDetails({
                  ...appointmentDetails,
                  postCode: e.target.value,
                })
                addContactDetails({
                  ...contactDetails,
                  postcode: e.target.value,
                })
                if (checkForNoErrors) {
                  setValidPostcode(true)
                  setPostCodeError(false)
                } else {
                  setValidPostcode(false)
                  setPostCodeError(true)
                }
                setValidPostCodeError(false)  
              }}
              errors={errors}
            />
            {postCodeError && checkForNoErrors && !validPostcode ? (
              <div className="post-code-error">
                <p>Please enter a valid postcode</p>
              </div>
            ) : null}
            {validPostCodeError ? (
              <div className="post-code-error">
                <p>Something went wrong. Please check your postcode for errors and try again, or try a different postcode.</p>
              </div>
            ) : null}
            <div className="button-container">
              <GeoLocation
                postCode={postCode}
                setPostCodeError={setPostCodeError}
                groupId={GroupIds[groupId]}
                addClosestDealerships={addClosestDealerships}
                closestDealerships={closestDealerships}
                dealershipCtaDisabled={dealershipCtaDisabled}
                setDealershipCtaDisabled={setDealershipCtaDisabled}
                rendersFindDealershipCta={false}
                validPostcode={validPostcode}
              />
            </div>
            <Seperator />
            <TermsAndConditions
              termsConditions={termsConditions}
              addTermsConditions={addTermsConditions}
              name="termsConditions"
              register={register}
              setValue={setValue}
              errors={errors}
            />
            <div>
              {errors && errors["termsConditions"] ? (
                <p>Please accept the terms</p>
              ) : null}
            </div>
            <Seperator />
            <Button
              onClick={() => {
                getValuationLimit(history)
              }}
              type="submit"
              variant="blue"
              id="valuation-button"
              disabled={!checkForEmptyFields || !checkForNoErrors || validPostCodeError}
            >
              SEE VALUATION
            </Button>
            <Link className="text-link" to={`/vehicle-details${checkPath(search)}`}>
              <Button disabled={false} type="button" variant="white">
                VEHICLE DETAILS
              </Button>
            </Link>
          </form>
        </div>
        <TextLink text="Change vehicle" href="/" />
      </div>
    </div>
  )
}

export default YourDetails
